<template>
    <div>
        <div class="right p-2" v-if="updating">
            <loader />
        </div>
        <div class="right flex flex-row pt-4 px-4" v-else-if="resetsIn">
            <circular-loader
                class="mr-2"
                :width="34"
                :percentage="(user.subscription.inspections.count/user.subscription.inspections.limit)*100"
            />
            <span class="flex flex-col my-auto">
                <span class="font-semibold">
                    {{ `${user.subscription.inspections.count}/${user.subscription.inspections.limit} inspections` }}
                </span>
                <span>
                    {{ `Resets in ${resetsIn.time} ${resetsIn.unit}` }}
                </span>
            </span>
        </div>
    </div>
</template>

<script>
import Vue from 'vue';
import _ from 'lodash';
import { mapGetters } from 'vuex';
import { eventBus } from '@/eventBus';
import CircularLoader from '@mobile/components/CircularLoader.vue';
import { updateUserSubscriptionInspections } from '@/services/user';
import Loader from './Loader.vue';

const MS_PER_HOUR = 1000 * 60 * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;

export default {
    name: 'InspectionCount',
    components: { Loader, CircularLoader },
    props: {
        user: {
            type: Object,
            default: () => {},
        },
    },

    created() {
        eventBus.$on('inspections-sending', () => { this.updating = true; });
        eventBus.$on('inspections-updated', () => { this.updating = false; });
        this.setResetsIn();
        this.setResetInterval();
    },

    data() {
        return {
            resetsIn: {},
            reset: null,
            updating: false,
        };
    },

    beforeDestroy() {
        return this.reset && clearInterval(this.reset);
    },

    computed: {
        ...mapGetters({
            online: 'connection/online',
        }),
    },

    watch: {
        'user.subscription.inspections': {
            handler: 'setResetsIn',
            immediate: true,
            deep: true,
        },
        online: {
            handler: 'setResetInterval',
            immediate: true,
        },
    },

    methods: {
        setResetInterval() {
            this.reset = this.online
                ? clearInterval(this.reset)
                : setInterval(this.setResetsIn, 1000 * 60 * 30);
        },

        async setResetsIn() {
            const resetDate = this.user.subscription.inspections.resets_in;
            const resetsIn = await this.getResetsIn(resetDate);
            if (!_.isEqual(resetsIn, this.resetsIn)) {
                this.resetInspections(resetsIn, resetDate);
            }
            Vue.set(this, 'resetsIn', resetsIn);
        },

        resetInspections(resetsIn) {
            if (resetsIn.time <= 0) {
                const lastReset = new Date(this.user.subscription.inspections.resets_in * 1000);
                const nextReset = new Date(lastReset.setMonth(lastReset.getMonth() + 1))
                const inspections = _.cloneDeep(this.user.subscription.inspections);
                inspections.count = 0;
                inspections.resets_in = Math.ceil(Date.UTC(
                    nextReset.getFullYear(),
                    nextReset.getMonth(),
                    nextReset.getDate(),
                    nextReset.getHours(),
                    nextReset.getMinutes(),
                    nextReset.getSeconds(),
                    nextReset.getMilliseconds(),
                ) / 1000);
                inspections.limit_reached = false;
                updateUserSubscriptionInspections(inspections);
                this.$store.commit('authentication/setUserSubscriptionInspection', inspections);
            }
        },

        getResetsIn(resetDate) {
            let unit = 'days';
            const today = new Date();
            const now = Date.UTC(
                today.getFullYear(),
                today.getMonth(),
                today.getDate(),
                today.getHours(),
                today.getMinutes(),
                today.getSeconds(),
                today.getMilliseconds(),
            );
            const diff = resetDate * 1000 - now;

            let resetsIn = Math.floor(diff / MS_PER_DAY);

            if (resetsIn <= 0) {
                resetsIn = Math.ceil(diff / MS_PER_HOUR);
                unit = 'hours';
            }

            return {
                time: resetsIn,
                unit,
            };
        },
    },
};
</script>
